import React, { FC, memo, useState } from 'react';
import { Circle, Group } from 'react-konva';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { VERTEX_HANDLE_RADIUS_PX, MIN_VERTEX_COUNT } from '../model/constants/polygon';
import { getSnapStepPixels } from '../model/selectors/getSnapStepPixels';
import { PolygonBoxShape, PolygonShape } from '../model/types';
import { computeMidpoints, pointsToVertices, verticesToPoints } from '../utils/helpers/polygonVertices';

interface Props {
  shape: PolygonShape | PolygonBoxShape;
  canvasScale: number;
  onChangePoints: (nextPoints: number[]) => void;
  onCommit: () => void;
  onCancel: () => void;
}

const HANDLE_COLOR = '#1677ff';
const MIDPOINT_OPACITY = 0.5;

export const PolygonVertexEditor: FC<Props> = memo(({ shape, canvasScale, onChangePoints, onCommit, onCancel }) => {
  const snapStepPixels = useAppSelector(getSnapStepPixels);

  const [editPoints, setEditPoints] = useState<number[]>(shape.points);

  const vertices = pointsToVertices(editPoints);
  const midpoints = computeMidpoints(vertices);

  const radius = VERTEX_HANDLE_RADIUS_PX / canvasScale;
  const midRadius = (VERTEX_HANDLE_RADIUS_PX - 1) / canvasScale;
  const sw = 1 / canvasScale;

  const applyVertexMove = (index: number, x: number, y: number): void => {
    const snappedX = Math.round(x / snapStepPixels) * snapStepPixels;
    const snappedY = Math.round(y / snapStepPixels) * snapStepPixels;
    const next = [...editPoints];

    next[index * 2] = snappedX;
    next[index * 2 + 1] = snappedY;
    setEditPoints(next);
    onChangePoints(next);
  };

  const removeVertex = (index: number): void => {
    if (vertices.length <= MIN_VERTEX_COUNT) return;

    const next = [...editPoints];

    next.splice(index * 2, 2);
    setEditPoints(next);
    onChangePoints(next);
  };

  const insertMidpoint = (insertIndex: number, x: number, y: number): void => {
    const snappedX = Math.round(x / snapStepPixels) * snapStepPixels;
    const snappedY = Math.round(y / snapStepPixels) * snapStepPixels;
    const nextVertices = [...vertices];

    nextVertices.splice(insertIndex, 0, { x: snappedX, y: snappedY });
    const next = verticesToPoints(nextVertices);

    setEditPoints(next);
    onChangePoints(next);
  };

  return (
    <Group rotation={shape.rotation} scaleX={shape.scaleX} scaleY={shape.scaleY}>
      {/* Midpoint handles — click to insert a vertex */}
      {midpoints.map((m, i) => (
        <Circle
          key={`mid-${i}`}
          name="midpoint-handle"
          x={m.x}
          y={m.y}
          radius={midRadius}
          fill="white"
          stroke={HANDLE_COLOR}
          strokeWidth={sw}
          strokeScaleEnabled={false}
          opacity={MIDPOINT_OPACITY}
          onClick={(e) => {
            e.cancelBubble = true;
            insertMidpoint(m.insertIndex, m.x, m.y);
          }}
        />
      ))}

      {/* Vertex handles — draggable; Alt+click to delete */}
      {vertices.map((v, i) => (
        <Circle
          key={`v-${i}`}
          name="vertex-handle"
          x={v.x}
          y={v.y}
          radius={radius}
          fill="white"
          stroke={HANDLE_COLOR}
          strokeWidth={sw}
          strokeScaleEnabled={false}
          draggable
          onDragMove={(e) => {
            applyVertexMove(i, e.target.x(), e.target.y());
          }}
          onDragEnd={(e) => {
            applyVertexMove(i, e.target.x(), e.target.y());
          }}
          onClick={(e) => {
            if (e.evt.altKey) {
              e.cancelBubble = true;
              removeVertex(i);
            }
          }}
        />
      ))}
    </Group>
  );
});
